import React, { useEffect, useRef, useState } from "react";
import { Plus, X, ChevronDown } from "lucide-react";

import Select from "../select";
import DateRangeInput from "../dateRangeInput";
import { BLANK_FILTER_VALUE } from "./useFilterChips";
import styles from "./filterBar.module.scss";

/**
 * The multi-filter row under a list's search box.
 *
 * Chips are field/value pairs — "Profession is Author" — added from the
 * page's own `filterOptions`, so a page gains filters by listing a field, not
 * by writing UI.
 *
 * Match all / Match any: "all" is what every list endpoint already does (each
 * named filter field lands in the same `$match`). "any" is passed to the
 * server as `matchMode` — the toggle is not cosmetic, and a backend that
 * ignores it will silently AND, so check the endpoint before offering it.
 */

/** A chip's value as a list, whichever shape it is currently holding. */
const asList = (value) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

/** A suggestion as a react-select option: a bare name is its own label. */
const toOption = (suggestion) =>
  typeof suggestion === "string" ? { label: suggestion, value: suggestion } : suggestion;

/**
 * A field's suggestions as react-select options. Each item is a bare name, a
 * `{ value, label }`, or a group `{ label, options }` — the audio settings
 * filter lists every setting in one dropdown, headed by the setting's name.
 */
const toOptions = (suggestions = []) =>
  suggestions.map((s) =>
    s && Array.isArray(s.options)
      ? { label: s.label, options: s.options.map(toOption) }
      : toOption(s)
  );

// react-select sizes itself in JS, so the chip's own CSS cannot reach it.
// Sized to sit inside a chip rather than stand alone as a form field.
const chipSelectStyles = {
  container: (base) => ({ ...base, minWidth: 240 }),
  control: (base) => ({ ...base, minHeight: 32, fontSize: 12 }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  placeholder: (base) => ({ ...base, fontSize: 12 }),
  multiValue: (base) => ({ ...base, margin: "2px" }),
  menu: (base) => ({ ...base, fontSize: 12, zIndex: 40 }),
};

// Fields the server reads as dates, so the chip offers a from/to pair rather
// than a text box. Named here because more than one list column is a date now.
const DATE_FIELDS = new Set(["createdAt", "updatedAt", "audioGeneratedOn"]);

const FilterBar = ({
  options,
  // Whether this page's endpoint honours `matchMode: "any"`. Off by default,
  // and the toggle is hidden when it is off: a control that silently ANDs
  // whatever you pick is worse than no control. Turn it on per page only once
  // the endpoint builds its clauses as $or branches.
  supportsMatchAny = false,
  chips,
  matchMode,
  onMatchModeChange,
  onAdd,
  onUpdate,
  onRemove,
  onClear,
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const addRef = useRef(null);

  // A dropdown that only closes on its own button is a dropdown people leave
  // open by accident.
  useEffect(() => {
    if (!isAddOpen) return undefined;
    const onDocClick = (e) => {
      if (addRef.current && !addRef.current.contains(e.target)) {
        setIsAddOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setIsAddOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isAddOpen]);

  const labelFor = (field) =>
    options.find((o) => o.value === field)?.label || field;

  /** The fixed answers for an enumerated field, or undefined for a free one. */
  const choicesFor = (field) => options.find((o) => o.value === field)?.choices;

  /** The managed values a free-text field draws from, if it has a list. */
  const suggestionsFor = (field) =>
    options.find((o) => o.value === field)?.suggestions;

  // Every chip can ask for rows where its field is empty, except three kinds.
  // A fixed-choice field already names its empty state ("Not narrated", "No
  // video"), so a second way to say it would only disagree with the first. A
  // date never is empty — every record is timestamped. And the sticky name
  // chip is the list's search box: a book with no name is not something
  // anyone is looking for. A field can also opt out with `noBlank` when a
  // value is more than a value — an audio-settings pick names its setting too.
  const canBeBlank = (chip) =>
    !chip.sticky &&
    !choicesFor(chip.field) &&
    !DATE_FIELDS.has(chip.field) &&
    !options.find((o) => o.value === chip.field)?.noBlank;

  // A field already on screen is not offered again. Two chips on one field
  // cannot both reach the server — each endpoint reads one named key per
  // field — so the second would quietly replace the first.
  const used = new Set(chips.map((c) => c.field));
  const available = options.filter((o) => !used.has(o.value));

  return (
    <div className={styles.filterBar}>
      {supportsMatchAny && (
        <div className={styles.matchToggle} role="group" aria-label="How filters combine">
          <button
            type="button"
            className={`${styles.matchButton} ${matchMode === "all" ? styles.matchActive : ""}`}
            onClick={() => onMatchModeChange("all")}
            aria-pressed={matchMode === "all"}
          >
            Match all
          </button>
          <button
            type="button"
            className={`${styles.matchButton} ${matchMode === "any" ? styles.matchActive : ""}`}
            onClick={() => onMatchModeChange("any")}
            aria-pressed={matchMode === "any"}
          >
            Match any
          </button>
        </div>
      )}

      {chips.map((chip) => {
        const isBlank = chip.value === BLANK_FILTER_VALUE;
        return (
            <div className={styles.chip} key={chip.id}>
              <span className={styles.chipField}>{labelFor(chip.field)}</span>
              {canBeBlank(chip) ? (
                // Switching back to "is" empties the chip rather than restoring
                // what it held: the old value was replaced, and a chip that came
                // back half-filled would filter on something nobody re-chose.
                <select
                  className={styles.chipVerbSelect}
                  value={isBlank ? "blank" : "is"}
                  onChange={(e) =>
                    onUpdate(chip.id, e.target.value === "blank" ? BLANK_FILTER_VALUE : "")
                  }
                  aria-label={`How to match ${labelFor(chip.field)}`}
                >
                  <option value="is">is</option>
                  <option value="blank">is blank</option>
                </select>
              ) : (
                <span className={styles.chipVerb}>is</span>
              )}
              {/* A field with a fixed set of answers gets a select, not a text
                  box: "Audio is Fully narrated" is a choice, and typing it free
                  hand can only produce a value the endpoint rejects. */}
              {isBlank ? (
                <span className={styles.chipBlankSpacer} />
              ) : DATE_FIELDS.has(chip.field) ? (
                <DateRangeInput
                  value={chip.value}
                  onChange={(v) => onUpdate(chip.id, v)}
                  className={styles.chipInput}
                  ariaLabel={labelFor(chip.field)}
                />
              ) : choicesFor(chip.field) ? (
                <select
                  className={styles.chipInput}
                  value={chip.value}
                  onChange={(e) => onUpdate(chip.id, e.target.value)}
                  aria-label={`${labelFor(chip.field)} filter value`}
                  autoFocus={chip.value === ""}
                >
                  <option value="">any</option>
                  {choicesFor(chip.field).map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              ) : suggestionsFor(chip.field) !== undefined ? (
                // Genre and profession take SEVERAL values, the same react-select
                // multi the add/edit forms use for the same fields. Keyed on the
                // field HAVING a list rather than the list being full, because the
                // names arrive from a fetch and swapping the control type under a
                // half-filled chip would throw away what was picked.
                <Select
                  isMulti
                  options={toOptions(suggestionsFor(chip.field) || [])}
                  // The chip holds bare values; show each by its option's label
                  // when the list has one, else by the value itself.
                  value={asList(chip.value).map((value) => {
                    const flat = toOptions(suggestionsFor(chip.field) || []).flatMap(
                      (o) => o.options || [o]
                    );
                    return flat.find((o) => o.value === value) || { label: value, value };
                  })}
                  // Several values on one field mean "any of these" — the server
                  // $ors them. Sent as an array, not a joined string, so a name
                  // containing a comma cannot split itself in two.
                  onChange={(picked) =>
                    onUpdate(chip.id, (picked || []).map((option) => option.value))
                  }
                  placeholder="any"
                  aria-label={`${labelFor(chip.field)} filter value`}
                  styles={chipSelectStyles}
                />
              ) : (
                <input
                  className={styles.chipInput}
                  type="text"
                  value={chip.value}
                  onChange={(e) => onUpdate(chip.id, e.target.value)}
                  placeholder="any"
                  aria-label={`${labelFor(chip.field)} filter value`}
                  // A chip added from the menu lands focused, so the next keystroke
                  // goes where the reader is already looking.
                  autoFocus={chip.value === ""}
                />
              )}
              {/* A sticky chip keeps its place; clearing it is what removing
                  means for the one filter the page is normally searched by. */}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onRemove(chip.id)}
                aria-label={
                  chip.sticky
                    ? `Clear ${labelFor(chip.field)} filter`
                    : `Remove ${labelFor(chip.field)} filter`
                }
                title={chip.sticky ? "Clear" : "Remove"}
              >
                <X size={14} />
              </button>
            </div>
        );
      })}

      <div className={styles.addWrapper} ref={addRef}>
        <button
          type="button"
          className={styles.addButton}
          onClick={() => setIsAddOpen((v) => !v)}
          aria-expanded={isAddOpen}
        >
          <Plus size={14} /> Add filter
          <ChevronDown size={14} />
        </button>
        {isAddOpen && (
          <div className={styles.addMenu} role="menu">
            {available.length === 0 && (
              <span className={styles.addMenuEmpty}>Every filter is in use</span>
            )}
            {available.map((option) => (
              <button
                key={option.value}
                type="button"
                className={styles.addMenuItem}
                role="menuitem"
                onClick={() => {
                  onAdd(option.value);
                  setIsAddOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {chips.length > 0 && (
        <button type="button" className={styles.clearAll} onClick={onClear}>
          Clear all
        </button>
      )}

    </div>
  );
};

export default FilterBar;
